// Profile photo panel: choose/drop a PNG/JPEG, crop to a square with a
// circular guide, zoom, pan (pointer or arrow keys), export 256px PNG.
// Cancel/Escape never write. Save failures keep the crop and show the
// server message verbatim with a Retry. Remove only appears for a custom photo.

import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, Upload } from "lucide-react";
import { LAYERS, T } from "../../app/tokens.js";
import { uploadProfilePhoto, removeProfilePhoto } from "../../data/operationsClient.js";
import { getInitials } from "../../utils/initials.js";
import { useAccountPhoto } from "../useAccountPhoto.js";
import {
  EXPORT_SIZE, MAX_ZOOM, MIN_ZOOM, clampOffset, clampZoom, coverScale,
  resolveAccountPhotoUrl, sourceRect, validatePhotoFile,
} from "./cropMath.js";

const VIEW = 200;
const KEY_STEP = 8;

export function exportCrop(img, zoom, offset, view = VIEW) {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const { sx, sy, size } = sourceRect(w, h, view, zoom, offset);
  const canvas = document.createElement("canvas");
  canvas.width = EXPORT_SIZE;
  canvas.height = EXPORT_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.reject(new Error("Image export is not supported in this browser."));
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, sx, sy, size, size, 0, 0, EXPORT_SIZE, EXPORT_SIZE);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Could not export the cropped image."))), "image/png");
  });
}

export function ProfilePhotoDialog({ currentUser, displayName, onClose, returnFocusRef }) {
  const titleId = useId();
  const dialogRef = useRef(null);
  const inputRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);
  const loadingRef = useRef(null);
  const actionRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState(null); // { url, w, h }
  const [zoom, setZoom] = useState(1.4);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(null); // "save" | "remove"
  const [error, setError] = useState(null); // { message, action }
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const currentUrl = resolveAccountPhotoUrl(currentUser);
  const hasCustom = Boolean(currentUser?.custom_photo_url);
  const { photoSrc, onPhotoError } = useAccountPhoto(currentUser?.id ?? currentUser?.email, currentUrl);
  const name = displayName || currentUser?.display_name || "";

  const close = useCallback(() => {
    if (busy) return;
    onCloseRef.current?.();
  }, [busy]);

  // Focus on open, return focus on unmount.
  useEffect(() => {
    const returnTarget = returnFocusRef?.current ?? null;
    dialogRef.current?.querySelector("[data-autofocus]")?.focus();
    return () => { returnTarget?.focus?.(); };
  }, [returnFocusRef]);

  useEffect(() => () => { if (source?.url) URL.revokeObjectURL(source.url); }, [source]);
  useEffect(() => () => {
    const pending = loadingRef.current;
    if (pending) {
      pending.img.onload = null;
      pending.img.onerror = null;
      URL.revokeObjectURL(pending.url);
      loadingRef.current = null;
    }
  }, []);

  function onDialogKeyDown(e) {
    if (e.key === "Escape") {
      e.stopPropagation();
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== "Tab") return;
    const nodes = [...dialogRef.current.querySelectorAll(
      'button:not([disabled]), input:not([disabled]):not([type="file"]), [tabindex="0"]',
    )];
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function pickFile(file) {
    if (actionRef.current) return;
    if (loadingRef.current) {
      const pending = loadingRef.current;
      pending.img.onload = null;
      pending.img.onerror = null;
      URL.revokeObjectURL(pending.url);
      loadingRef.current = null;
    }
    setLoading(false);
    const problem = validatePhotoFile(file);
    if (problem) { setError({ message: problem, action: null }); return; }
    setError(null);
    const url = URL.createObjectURL(file);
    const img = new Image();
    loadingRef.current = { img, url };
    setLoading(true);
    img.onload = () => {
      loadingRef.current = null;
      setLoading(false);
      setSource({ url, w: img.naturalWidth, h: img.naturalHeight });
      setZoom(1.4);
      setOffset({ x: 0, y: 0 });
    };
    img.onerror = () => {
      loadingRef.current = null;
      setLoading(false);
      URL.revokeObjectURL(url);
      setError({ message: "That image could not be read. Try another file.", action: null });
    };
    img.src = url;
  }

  function moveBy(dx, dy) {
    if (!source || actionRef.current) return;
    setOffset((o) => clampOffset({ x: o.x + dx, y: o.y + dy }, source.w, source.h, VIEW, zoom));
  }

  function changeZoom(next) {
    if (actionRef.current) return;
    const z = clampZoom(next);
    setZoom(z);
    if (source) setOffset((o) => clampOffset(o, source.w, source.h, VIEW, z));
  }

  function onPointerDown(e) {
    if (!source || actionRef.current || (e.button !== undefined && e.button !== 0) || dragRef.current) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { id: e.pointerId, x: e.clientX, y: e.clientY };
  }
  function onPointerMove(e) {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    moveBy(e.clientX - d.x, e.clientY - d.y);
    dragRef.current = { ...d, x: e.clientX, y: e.clientY };
  }
  function endDrag(e) {
    if (dragRef.current?.id !== e.pointerId) return;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    dragRef.current = null;
  }
  function onCropKeyDown(e) {
    const step = e.shiftKey ? KEY_STEP * 4 : KEY_STEP;
    const map = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
    if (map[e.key]) { e.preventDefault(); moveBy(...map[e.key]); }
    else if (e.key === "+" || e.key === "=") { e.preventDefault(); changeZoom(zoom + 0.1); }
    else if (e.key === "-") { e.preventDefault(); changeZoom(zoom - 0.1); }
  }

  async function save() {
    if (!source || !imgRef.current || loading || actionRef.current) return;
    actionRef.current = true;
    setBusy("save");
    setError(null);
    try {
      const blob = await exportCrop(imgRef.current, zoom, offset);
      await uploadProfilePhoto(blob);
      setBusy(null);
      actionRef.current = false;
      onCloseRef.current?.();
    } catch (err) {
      setBusy(null);
      actionRef.current = false;
      setError({ message: err?.message || "Upload failed.", action: "save" });
    }
  }

  async function remove() {
    if (actionRef.current) return;
    actionRef.current = true;
    setBusy("remove");
    setError(null);
    try {
      await removeProfilePhoto();
      setBusy(null);
      actionRef.current = false;
      onCloseRef.current?.();
    } catch (err) {
      setBusy(null);
      actionRef.current = false;
      setError({ message: err?.message || "Could not remove photo.", action: "remove" });
    }
  }

  const scale = source ? coverScale(source.w, source.h, VIEW) * zoom : 0;
  const dw = source ? source.w * scale : 0;
  const dh = source ? source.h * scale : 0;
  const previewSrc = photoSrc;

  return createPortal(
    <div
      data-testid="profile-photo-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
      style={{
        position: "fixed", inset: 0, zIndex: LAYERS.modal,
        background: T.modalOverlay, display: "grid", placeItems: "center", padding: 16,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="profile-photo-dialog"
        onKeyDown={onDialogKeyDown}
        style={{
          width: 320, maxWidth: "100%", background: "var(--v2-card)", color: "var(--v2-dark)",
          border: "1px solid var(--v2-line)", borderRadius: 12, boxShadow: T.shadowAsk, padding: 16,
          maxHeight: "calc(100dvh - 32px)", overflowY: "auto",
        }}
      >
        <h2 id={titleId} style={{ margin: "0 0 12px", fontSize: T.fsBase, fontWeight: T.fwHubTitle }}>
          Profile photo
        </h2>

        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 13 }}>
          <span
            data-testid="profile-photo-current"
            style={{
              width: 64, height: 64, borderRadius: "50%", overflow: "hidden", flex: "none",
              background: "var(--v2-dark)", color: T.white, fontSize: T.fsLgX, fontWeight: T.fwHubTitle,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            {previewSrc
              ? <img src={previewSrc} onError={onPhotoError} referrerPolicy="no-referrer" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <span aria-hidden="true">{getInitials(name)}</span>}
          </span>
          <div style={{ fontSize: T.fsXs, color: "var(--v2-mid)" }} data-testid="profile-photo-source">
            {hasCustom ? "Uploaded photo" : currentUrl ? "Okta photo" : "Initials"}
            <div>Uploads override your Okta photo.</div>
          </div>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg"
          data-testid="profile-photo-input"
          style={{ display: "none" }}
          onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ""; }}
        />
        <button
          type="button"
          disabled={Boolean(busy)}
          data-autofocus
          data-testid="profile-photo-drop"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer?.files?.[0]); }}
          style={{
            width: "100%", marginBottom: 13, padding: 16, borderRadius: 10, cursor: "pointer",
            border: `1.5px dashed ${dragOver ? "var(--v2-accent)" : T.borderDark}`,
            background: dragOver ? "var(--v2-blue-tint)" : "var(--v2-tint)",
            color: "var(--v2-mid)", fontSize: T.fsSm, textAlign: "center",
          }}
        >
          <Upload size={20} aria-hidden="true" style={{ display: "block", margin: "0 auto 6px", color: "var(--v2-accent)" }} />
          <b style={{ color: "var(--v2-accent)" }}>{source ? "Choose another image" : "Click to upload"}</b> or drag an image here
        </button>

        {source && (
          <>
            <div style={{ fontSize: T.fsXxs5, fontWeight: T.fwBold, color: "var(--v2-mid)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 }}>
              Crop and zoom
            </div>
            <div
              tabIndex={busy ? -1 : 0}
              role="group"
              aria-label="Crop area. Drag or use arrow keys to reposition; plus and minus to zoom."
              data-testid="profile-photo-crop"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onLostPointerCapture={() => { dragRef.current = null; }}
              onKeyDown={onCropKeyDown}
              style={{
                position: "relative", width: VIEW, height: VIEW, margin: "0 auto",
                borderRadius: 10, overflow: "hidden", background: "var(--v2-dark)",
                cursor: "grab", touchAction: "none", outlineOffset: 2,
              }}
            >
              <img
                ref={imgRef}
                src={source.url}
                alt="Selected photo"
                draggable={false}
                style={{
                  position: "absolute", left: 0, top: 0, width: dw, height: dh, maxWidth: "none",
                  transform: `translate(${VIEW / 2 + offset.x - dw / 2}px, ${VIEW / 2 + offset.y - dh / 2}px)`,
                  userSelect: "none", pointerEvents: "none",
                }}
              />
              <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at center, transparent ${VIEW / 2 - 2}px, ${T.photoScrim} ${VIEW / 2}px)` }} />
              <div aria-hidden="true" style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `2px solid ${T.white}` }} />
              <div aria-hidden="true" style={{ position: "absolute", left: 0, right: 0, bottom: 8, textAlign: "center", color: T.whiteHover, fontSize: T.fsXxs, fontWeight: T.fwSemibold }}>
                drag to reposition
              </div>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 0 2px", color: "var(--v2-mid)", fontWeight: T.fwBold }}>
              <span aria-hidden="true">-</span>
              <input
                type="range"
                aria-label="Zoom"
                data-testid="profile-photo-zoom"
                min={MIN_ZOOM} max={MAX_ZOOM} step={0.01} value={zoom}
                disabled={Boolean(busy)}
                onChange={(e) => changeZoom(e.target.value)}
                style={{ flex: 1, accentColor: "var(--v2-accent)" }}
              />
              <span aria-hidden="true">+</span>
            </label>
          </>
        )}
        <div style={{ fontSize: T.fsXxs5, color: "var(--v2-light)", marginTop: 6, textAlign: "center" }}>
          Square crop · exported at 256px · PNG/JPG, up to 5MB
        </div>

        {error && (
          <div role="alert" data-testid="profile-photo-error" style={{
            marginTop: 12, padding: "8px 10px", borderRadius: 8, fontSize: T.fsSm,
            background: T.alertErrBg, border: `1px solid ${T.alertErrBorder}`, color: T.ragRed,
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{ flex: 1 }}>{error.message}</span>
            {error.action && (
              <button type="button" disabled={Boolean(busy)} data-testid="profile-photo-retry" onClick={error.action === "save" ? save : remove} style={{ ...btn, padding: "3px 9px" }}>
                Retry
              </button>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button type="button" data-testid="profile-photo-save" disabled={!source || loading || Boolean(busy)} onClick={save}
            style={{ ...btn, background: "var(--v2-accent)", borderColor: "var(--v2-accent)", color: T.white, opacity: !source || busy || loading ? 0.55 : 1 }}>
            {busy === "save" ? "Saving..." : loading ? "Reading..." : "Save"}
          </button>
          <button type="button" data-testid="profile-photo-cancel" disabled={Boolean(busy)} onClick={close} style={btn}>
            Cancel
          </button>
          {hasCustom && (
            <button type="button" data-testid="profile-photo-remove" disabled={Boolean(busy)} onClick={remove}
              style={{ ...btn, marginLeft: "auto", color: T.ragRed, borderColor: T.ragRedBorder }}>
              {busy === "remove" ? "Removing..." : "Remove photo"}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

const btn = {
  border: "1px solid var(--v2-line)", background: "var(--v2-card)", color: "var(--v2-dark)",
  borderRadius: 8, padding: "7px 13px", fontSize: T.fsSm, fontWeight: T.fwBold, cursor: "pointer",
};

/** Avatar with hover/focus "Change" overlay used in the account menu. */
export function ChangePhotoAvatar({ children, onActivate, size = 38 }) {
  const [hot, setHot] = useState(false);
  return (
    <button
      type="button"
      role="menuitem"
      aria-label="Change photo"
      title="Change photo"
      data-testid="v2-account-avatar-change"
      onClick={onActivate}
      onMouseEnter={() => setHot(true)}
      onMouseLeave={() => setHot(false)}
      onFocus={() => setHot(true)}
      onBlur={() => setHot(false)}
      style={{ position: "relative", width: size, height: size, flex: "none", padding: 0, border: 0, background: "transparent", borderRadius: "50%", cursor: "pointer" }}
    >
      {children}
      <span aria-hidden="true" style={{
        position: "absolute", inset: 0, borderRadius: "50%", background: T.photoScrim, color: T.white,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        fontSize: T.fsMin5, fontWeight: T.fwBold, gap: 1, opacity: hot ? 1 : 0, transition: "opacity .12s",
      }}>
        <Camera size={14} />Change
      </span>
    </button>
  );
}
